import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./SuperChatAIOrderTable.scss";
import { fetchOrderById } from "../../../../../store/slices/ordersSlice";
import { routes } from "../../../../../routes/routes.constatns";
import { SortIcon } from "../../../../../assets/Icons/SortIcon";
import { FilterList } from "../../../../../assets/Icons/FilterList";
import { ExportIcon } from "../../../../../assets/Icons/ExportIcon";
import { exportTableData, getSuggestionExportFileName } from "./tableExport";
import { TableSearch, searchTableRows } from "./TableSearch";

export const RegionalLeadOrderTable = ({
  orders,
  message,
  onAddUserMessage,
  actionNeededCount,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  // Check if this is an allocation review request to show Reason column
  const isAllocationReviewRequest =
    message?.staticViewType === "allocation_review_request";

  // Check if this is an ageing order request to show Ageing In column
  const isAgeingOrderRequest = message?.staticViewType === "aging_orders";

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // State for filtering
  const [filters, setFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const filterDropdownRef = useRef(null);

  // State for search
  const [searchTerm, setSearchTerm] = useState("");

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setActiveFilterColumn(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Define the exact column order for Regional Lead - always show all columns
  // Add "reason" column only for allocation review requests
  // Add "ageingIn" column as first column for ageing order requests
  const columns = useMemo(() => {
    const baseColumns = [];

    // Add ageingIn as first column if this is an ageing order request
    if (isAgeingOrderRequest) {
      baseColumns.push("agingInDays");
    }

    baseColumns.push("priority", "axonId", "clientEntity", "status");

    // Add reason column after clientEntity if this is an allocation review request
    if (isAllocationReviewRequest) {
      baseColumns.push("allocationReason");
    }

    // Add remaining columns
    baseColumns.push(
      "preparerName",
      "reviewerName",
      "approverName",
      "analyzer",
      "superRegion",
      "country",
      "dueDate",
      "iteration",
    );

    // Add requestCreationDate and submittedDate as the last columns for allocation review requests
    if (isAllocationReviewRequest) {
      baseColumns.push("requestCreationDate", "createdOn");
    }

    return baseColumns;
  }, [isAllocationReviewRequest, isAgeingOrderRequest]);

  // Columns that should not have sort/filter (only axonId)
  const excludedColumns = [];

  // Create a mapping for display names
  const getDisplayName = (key) => {
    const displayNames = {
      agingInDays: "Ageing In",
      priority: "Priority",
      axonId: "AXON ID",
      clientEntity: "Client-Entity",
      status: "Status",
      preparerName: "Preparer",
      reviewerName: "Reviewer",
      approverName: "Approver",
      analyzer: "Analyzer",
      superRegion: "Super Region",
      country: "Region",
      dueDate: "Due Date",
      iteration: "Iteration",
      allocationReason: "Reason",
      requestCreationDate: "Requested Date",
      createdOn: "Submitted Date",
    };
    return displayNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Get distinct values for a column
  const getDistinctValues = (columnKey) => {
    if (!orders || orders.length === 0) return [];
    const values = orders
      .map((order) => order[columnKey])
      .filter((value) => value !== null && value !== undefined && value !== "");
    return [...new Set(values)].sort();
  };

  // Apply search, sorting and filtering to orders
  const processedOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    let result = [...orders];

    result = searchTableRows(result, columns, searchTerm, (order, column) =>
      column === "axonId" && order[column] != null
        ? `AX-${order[column]}`
        : order[column],
    );

    // Apply filters
    Object.keys(filters).forEach((columnKey) => {
      const filterValues = filters[columnKey];
      if (filterValues && filterValues.length > 0) {
        result = result.filter((order) =>
          filterValues.includes(order[columnKey]),
        );
      }
    });

    // Apply sorting
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        // Custom sorting for priority column - extract numeric part after 'P'
        if (sortConfig.key === "priority") {
          // Extract numeric value from priority (e.g., "P1" -> 1, "P10" -> 10)
          const extractPriorityNumber = (val) => {
            if (!val) return Infinity; // Put empty values at the end
            const match = String(val).match(/\d+/);
            return match ? parseInt(match[0], 10) : Infinity;
          };
          const aNum = extractPriorityNumber(aValue);
          const bNum = extractPriorityNumber(bValue);

          if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
          if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        // Custom sorting for axonId column - extract numeric part after 'AX-'
        if (sortConfig.key === "axonId") {
          // Extract numeric value from axonId (e.g., "AX-123" -> 123, "12345" -> 12345)
          const extractAxonIdNumber = (val) => {
            if (!val) return Infinity; // Put empty values at the end
            const strVal = String(val).replace(/^AX-/i, ""); // Remove "AX-" prefix if present
            const num = parseInt(strVal, 10);
            return isNaN(num) ? Infinity : num;
          };
          const aNum = extractAxonIdNumber(aValue);
          const bNum = extractAxonIdNumber(bValue);

          if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
          if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [orders, filters, sortConfig, searchTerm, columns]);

  // Early return after all hooks
  if (!orders || orders.length === 0) return null;

  const handleExport = () => {
    exportTableData({
      rows: processedOrders,
      columns,
      getDisplayName,
      fileName: getSuggestionExportFileName(message?.staticViewType),
      sheetName: "Orders",
      getValue: (order, column) =>
        column === "axonId" ? `AX-${order[column]}` : order[column] ?? "-",
    }).catch((error) => console.error("Failed to export regional lead orders", error));
  };

  // Handle sort click
  const handleSort = (columnKey) => {
    if (excludedColumns.includes(columnKey)) return;

    let direction = "asc";
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null; // Reset to original order
      }
    }
    setSortConfig({ key: direction ? columnKey : null, direction });
  };

  // Handle filter toggle
  const handleFilterClick = (e, columnKey) => {
    e.stopPropagation();
    if (excludedColumns.includes(columnKey)) return;
    setActiveFilterColumn(activeFilterColumn === columnKey ? null : columnKey);
  };

  // Handle filter selection
  const handleFilterSelect = (columnKey, value) => {
    setFilters((prev) => {
      const currentFilters = prev[columnKey] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter((v) => v !== value)
        : [...currentFilters, value];

      if (newFilters.length === 0) {
        const { [columnKey]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: newFilters };
    });
  };

  // Clear all filters for a column
  const handleClearColumnFilter = (columnKey) => {
    setFilters((prev) => {
      const { [columnKey]: removed, ...rest } = prev;
      return rest;
    });
  };

  // Select all filters for a column
  const handleSelectAllFilter = (columnKey) => {
    const allValues = getDistinctValues(columnKey);
    setFilters((prev) => ({ ...prev, [columnKey]: allValues }));
  };

  const handleOrderClick = (order) => {
    // Dispatch to fetch order data
    dispatch(
      fetchOrderById({
        orderId: order.guid,
        roleId: selectedRole?.roleId || "",
      }),
    );

    // Navigate directly to the order details page
    // Pass a unique superAgentNavigationId to force fresh query API call each time
    // The order details page Chatbot will automatically call the query API to fetch the summary
    navigate(`/orders/${order.guid}`, {
      state: {
        orderDetails: order,
        superAgentNavigationId: Date.now(),
      },
    });
  };

  const handleSeeAllOrders = () => {
    navigate(`/${routes.orders}`);
  };

  // Render sort icon with active state
  const renderSortIcon = (columnKey) => {
    const isActive = sortConfig.key === columnKey;
    const color = isActive ? "#ffe600" : "#737387";
    return (
      <span
        className={`header-sort-icon ${isActive ? "active" : ""}`}
        onClick={() => handleSort(columnKey)}
        title={`Sort by ${getDisplayName(columnKey)}`}
      >
        <SortIcon width={14} height={14} color={color} />
      </span>
    );
  };

  return (
    <div className="regional-lead-table-container">
      {/* Search bar at top right */}
      <div className="regional-lead-table-header">
        <TableSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search orders..." />
        <button
          type="button"
          className="smart-table-export-btn"
          onClick={handleExport}
          disabled={processedOrders.length === 0}
        >
          <ExportIcon width={14} height={14} />
          Export
        </button>
      </div>
      <div className="super-agent-order-table-wrapper regional-lead-scroll">
        <table className="super-agent-order-table regional-lead-table">
          <thead>
          <tr className="super-agent-order-header">
            {columns.map((column) => (
              <th
                key={column}
                className={`super-agent-header-cell ${column}`}
              >
                <span className="header-cell-content">
                  <span className="header-cell-text">
                    {getDisplayName(column)}
                  </span>
                  <span className="header-cell-icons">
                    {renderSortIcon(column)}
                  </span>
                </span>
              </th>
            ))}
          </tr>
          </thead>
          <tbody>
          {processedOrders.map((order, idx) => (
            <tr key={order.guid || idx} className="super-agent-order-row">
              {columns.map((column) => {
                const value = order[column];
                const isAxonId = column === "axonId";

                return (
                  <td
                    key={column}
                    className={`super-agent-cell ${column} ${
                      isAxonId ? "super-agent-order-link" : ""
                    }`}
                    onClick={
                      isAxonId ? () => handleOrderClick(order) : undefined
                    }
                    title={value}
                  >
                    {isAxonId ? `AX-${value}` : value || "-"}
                  </td>
                );
              })}
            </tr>
          ))}
          </tbody>
        </table>
      </div>

      {actionNeededCount > 0 && (
        <div className="super-agent-action-needed-message">
          However there {actionNeededCount === 1 ? "is" : "are"}{" "}
          {actionNeededCount} order{actionNeededCount === 1 ? "" : "s"} where
          the User's action is needed.
        </div>
      )}
    </div>
  );
};
